/**
 * Tab Session Utilities
 *
 * Provides per-tab session control using sessionStorage (tab-scoped)
 * and BroadcastChannel for real-time cross-tab communication.
 *
 * Design decisions:
 * - sessionStorage is tab-isolated (destroyed on tab close, not shared between tabs)
 * - BroadcastChannel is explicit and bidirectional, avoiding storage event race conditions
 * - tabId is regenerated on app mount to handle Ctrl+D (duplicate tab) edge case
 */

export const ACTIVE_TAB_KEY = 'apins-active-tab'
export const TAB_ID_KEY = 'apins-tab-id'
export const SESSION_CHANNEL_NAME = 'apins-session'

export const SESSION_MESSAGES = {
  SESSION_TAKEN: 'SESSION_TAKEN',
}

/**
 * Returns the unique ID for the current tab.
 * Creates one if it doesn't exist yet.
 */
export const getTabId = () => {
  if (!sessionStorage.getItem(TAB_ID_KEY)) {
    sessionStorage.setItem(TAB_ID_KEY, crypto.randomUUID())
  }
  return sessionStorage.getItem(TAB_ID_KEY)
}

/**
 * Regenerates the tab ID. Should be called on app mount
 * to handle the Ctrl+D (duplicate tab) edge case, where
 * sessionStorage is inherited from the original tab.
 */
export const refreshTabId = () => {
  const newId = crypto.randomUUID()
  sessionStorage.setItem(TAB_ID_KEY, newId)
  return newId
}

/**
 * Marks this tab as the active session owner in localStorage
 * and notifies all other tabs via BroadcastChannel.
 */
export const claimActiveTab = () => {
  const tabId = getTabId()
  localStorage.setItem(ACTIVE_TAB_KEY, tabId)

  const channel = new BroadcastChannel(SESSION_CHANNEL_NAME)
  channel.postMessage({ type: SESSION_MESSAGES.SESSION_TAKEN, tabId })
  channel.close()
}

/**
 * Returns true if the current tab is the registered active session owner.
 */
export const isActiveTab = () => {
  return localStorage.getItem(ACTIVE_TAB_KEY) === getTabId()
}

/**
 * Clears the active tab registration from localStorage.
 */
export const clearActiveTab = () => {
  localStorage.removeItem(ACTIVE_TAB_KEY)
}
